<template>
  <div class="flex flex-col">
    <div
      class="promo-blue-gradient -mb-6 flex items-center justify-center gap-1 overflow-hidden rounded-t-3xl pt-2 pr-3 pb-7 pl-2"
    >
      <ArrowUpIcon class="text-white" />
      <span class="text-sm font-semibold text-white">
        {{
          isPromoApplicable
            ? t('cashbackInfo.promo-applicable-title')
            : t('cashbackInfo.promo-title')
        }}
      </span>
    </div>

    <div
      class="promo-blue-gradient rounded-3xl p-0.5 shadow-[0px_5px_10px_rgba(0,0,0,0.05)]"
    >
      <div
        class="bg-background-neutral-secondary space-y-4 rounded-[22px] px-4 pt-4"
      >
        <template v-if="!isPromoApplicable">
          <div class="space-y-2">
            <p class="text-text-neutral-secondary text-lg font-semibold">
              {{ t('cashbackInfo.your-cashback') }}
            </p>

            <div class="flex items-center gap-2">
              <div
                class="bg-background-neutral-primary flex flex-col rounded-[20px] px-3 py-2"
              >
                <span class="text-text-neutral-tertiary text-xs">
                  {{ t('cashbackInfo.promo-current') }}
                </span>
                <span
                  class="freedom-loyalty-text inline-block text-lg font-bold"
                >
                  {{ currentCashback }}%
                </span>
              </div>

              <span class="text-text-neutral-tertiary shrink-0 text-lg">→</span>

              <div class="promo-blue-gradient shrink-0 rounded-[20px] p-[2px]">
                <div
                  class="promo-locked-pill-tint bg-background-neutral-secondary flex flex-col rounded-[18px] px-3 py-2"
                >
                  <span
                    class="text-text-neutral-tertiary text-xs whitespace-nowrap"
                  >
                    {{ t('cashbackInfo.promo-after-purchase') }}
                  </span>
                  <div class="flex items-center gap-1">
                    <LockIcon class="text-text-neutral-tertiary shrink-0" />
                    <span class="text-text-neutral-tertiary text-lg font-bold">
                      {{ maxCashback }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <p class="text-text-neutral-tertiary text-xs">
              {{ t('cashbackInfo.promo-description') }}
            </p>
          </div>
        </template>

        <template v-else>
          <div class="space-y-1">
            <p class="text-text-neutral-secondary text-lg font-semibold">
              {{ t('cashbackInfo.your-cashback') }}
            </p>
            <div class="flex items-baseline gap-2">
              <span
                class="freedom-loyalty-text inline-block text-2xl font-bold"
              >
                {{ maxCashback }}%
              </span>
              <span
                class="text-text-neutral-tertiary text-lg font-semibold line-through"
              >
                {{ currentCashback }}%
              </span>
            </div>
            <p class="text-text-neutral-secondary text-xs">
              {{ t('cashbackInfo.promo-applicable-description') }}
            </p>
          </div>
        </template>

        <div class="border-situational-lightgrey border-t" />

        <CashbackInfoLevels
          :cashback-levels="cashbackLevels"
          :is-promo-applicable="isPromoApplicable"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import type { CashbackLevel } from '../types';
  import ArrowUpIcon from '../assets/20/arrow-up.svg?component';
  import LockIcon from '../assets/16/lock.svg?component';
  import CashbackInfoLevels from './CashbackInfoLevels.vue';
  import messages from '../locales.json';

  const props = defineProps<{
    cashbackLevels: CashbackLevel[];
    isPromoApplicable?: boolean;
  }>();

  const { t } = useI18n({ messages });

  const currentCashback = computed(
    () => props.cashbackLevels.find(l => l.isActive)?.cashback ?? 0,
  );

  const maxCashback = computed(() =>
    Math.max(0, ...props.cashbackLevels.map(l => l.cashback)),
  );
</script>

<style scoped>
  .promo-blue-gradient {
    background: linear-gradient(
      96.56deg,
      rgb(79, 105, 255) 1.15%,
      rgb(125, 171, 255) 100%
    );
  }

  .promo-locked-pill-tint {
    background-image: linear-gradient(
      92.75deg,
      rgba(79, 105, 255, 0.1) 1.15%,
      rgba(125, 171, 255, 0.1) 100%
    );
  }
</style>
